import { isAxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { isClientRuntime } from '@skin-seoul/env-utils';
import { getServiceLogger } from './serviceLogger';

interface TimedRequestConfig extends InternalAxiosRequestConfig {
  __loggingStartedAt?: number;
}

const formatRoute = (config: InternalAxiosRequestConfig): string => {
  const method = (config.method ?? 'get').toUpperCase();
  return `${method} ${config.url ?? ''}`;
};

const getDurationMs = (config: TimedRequestConfig | undefined): number => {
  const startedAt = config?.__loggingStartedAt;
  return startedAt ? Date.now() - startedAt : 0;
};

/**
 * Logs every outbound request/response with status and duration, server-side only.
 * Request bodies and params are never logged to keep sensitive values out of logs.
 */
export const attachServerLoggingInterceptor = (instance: AxiosInstance, clientName: string): void => {
  if (isClientRuntime()) {
    return;
  }

  instance.interceptors.request.use((config: TimedRequestConfig) => {
    config.__loggingStartedAt = Date.now();
    getServiceLogger().info(`[service/${clientName}] → ${formatRoute(config)}`);
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as TimedRequestConfig;
      getServiceLogger().info(`[service/${clientName}] ← ${response.status} ${formatRoute(config)} ${getDurationMs(config)}ms`);
      return response;
    },
    (error: unknown) => {
      if (!isAxiosError(error)) {
        getServiceLogger().error(`[service/${clientName}] ✕ non-axios error`, error instanceof Error ? error.message : error);
        return Promise.reject(error);
      }

      const config = error.config as TimedRequestConfig | undefined;
      const status = error.response?.status ?? error.code ?? 'ERR';
      const route = config ? formatRoute(config) : '<unknown route>';
      getServiceLogger().error(`[service/${clientName}] ✕ ${status} ${route} ${getDurationMs(config)}ms`, error.message);
      return Promise.reject(error);
    },
  );
};
